/**
 * Rule set preview chip. The discount rules app's preview link writes these cart attributes through
 * `/cart/update`; while they're set, the rule set function applies that draft instead of the active rule set.
 *
 * Shopify can serve a page rendered before the cart changed, so the Liquid hint alone shows a stale
 * chip after clearing and none right after opening a link. The chip trusts only `/cart.js`, and asks
 * for it only when there's a reason to think a preview is on, so other shoppers make no request.
 */
;(() => {
  const PREVIEW_ATTRIBUTE = '_IR_DISCOUNT_PREVIEW'
  const PREVIEW_NAME_ATTRIBUTE = '_IR_DISCOUNT_PREVIEW_NAME'
  const FALLBACK_NAME = 'Discount rules'
  const POSITION_KEY = 'ir-discount-preview-position'
  const ACTIVE_KEY = 'ir-discount-preview-active'
  const MARGIN = 12
  const root = window.Shopify?.routes?.root ?? '/'

  const storage = {
    get: (key) => {
      try {
        return localStorage.getItem(key)
      } catch {
        return null
      }
    },
    set: (key, value) => {
      try {
        if (value === null) localStorage.removeItem(key)
        else localStorage.setItem(key, value)
      } catch {}
    },
  }

  const hinted = Boolean(document.currentScript?.dataset.previewHint)
  // Preview links land on `/?cart=open`, which is when the served page is most likely to predate the preview.
  const landing = new URLSearchParams(window.location.search).has('cart')
  if (!hinted && !landing && !storage.get(ACTIVE_KEY)) return

  fetch(`${root}cart.js`, { cache: 'no-store', headers: { Accept: 'application/json' } })
    .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`cart.js responded ${res.status}`))))
    .then((cart) => {
      const attribute = (key) => (typeof cart.attributes?.[key] === 'string' ? cart.attributes[key].trim() : '')
      const id = attribute(PREVIEW_ATTRIBUTE)
      storage.set(ACTIVE_KEY, id ? '1' : null)
      if (id) render(id, attribute(PREVIEW_NAME_ATTRIBUTE) || FALLBACK_NAME)
    })
    .catch((error) => console.error('[ir-discount-preview] Failed to read the cart', error))

  function render(id, name) {
    const STYLES = `
      :host { all: initial; position: fixed; top: 12px; left: 12px; z-index: 2147483000; font-family: inherit; }
      * { box-sizing: border-box; }
      .wrap { position: relative; opacity: 0; transition: opacity 200ms ease-out; }
      .wrap.visible { opacity: 1; }
      .chip { display: flex; align-items: stretch; width: max-content; max-width: min(260px, calc(100vw - 24px));
        border-radius: 8px; background: #1d4ed8; color: #fff; box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.2), 0 4px 6px -4px rgb(0 0 0 / 0.2); }
      .label { display: flex; min-width: 0; align-items: center; gap: 6px; padding: 6px 10px 6px 8px; }
      .name { margin-left: 2px; }
      .grip { display: flex; flex-shrink: 0; align-items: center; border: 0; border-radius: 8px 0 0 8px; background: transparent;
        padding: 0 0 0 8px; color: #fff; opacity: 0.6; cursor: grab; touch-action: none; transition: opacity 150ms; }
      .grip:hover, .grip:focus-visible { opacity: 1; }
      .grip:active, .dragging .grip { cursor: grabbing; }
      .grip svg { width: 10px; height: 16px; }
      .dragging .chip { box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.3), 0 8px 10px -6px rgb(0 0 0 / 0.3); }
      .dot { position: relative; display: flex; width: 6px; height: 6px; flex-shrink: 0; }
      .dot span { position: absolute; inset: 0; border-radius: 9999px; background: #fff; }
      .dot .ping { opacity: 0.75; animation: ping 1s cubic-bezier(0, 0, 0.2, 1) infinite; }
      @keyframes ping { 75%, 100% { transform: scale(2); opacity: 0; } }
      .kicker { flex-shrink: 0; font-size: 10px; font-weight: 700; line-height: 1; letter-spacing: 0.09em; text-transform: uppercase; opacity: 0.7; }
      .name { overflow: hidden; white-space: nowrap; text-overflow: ellipsis; font-size: 14px; font-weight: 500; line-height: 1.25; }
      button { font: inherit; cursor: pointer; }
      button:disabled { cursor: default; }
      .clear { position: relative; display: flex; flex-shrink: 0; align-items: center; border: 0; border-left: 1px solid rgb(255 255 255 / 0.25);
        border-radius: 0 8px 8px 0; background: transparent; padding: 0 10px 0 6px; color: #fff; transition: background-color 150ms; }
      .clear::before { content: ''; position: absolute; inset: -10px; }
      .clear:hover { background: rgb(255 255 255 / 0.15); }
      .clear svg { position: relative; width: 16px; height: 16px; }
      .confirm { position: absolute; top: 100%; left: 0; margin-top: 6px; width: min(300px, calc(100vw - 24px)); padding: 12px;
        border: 1px solid #e5e7eb; border-radius: 4px; background: #fff; color: #111827; box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1); }
      .confirm.right { left: auto; right: 0; }
      .confirm.above { top: auto; bottom: 100%; margin: 0 0 6px; }
      .confirm[hidden] { display: none; }
      .question { margin: 0; font-size: 14px; font-weight: 500; line-height: 1.375; }
      .question b { overflow-wrap: anywhere; border-radius: 4px; background: #f3f4f6; padding: 1px 4px; -webkit-box-decoration-break: clone; box-decoration-break: clone; }
      .note { margin: 8px 0 0; font-size: 12px; line-height: 1.375; color: #4b5563; }
      .actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 12px; }
      .actions button { border-radius: 4px; padding: 4px 10px; font-size: 12px; font-weight: 500; }
      .cancel { border: 1px solid #e5e7eb; background: transparent; color: #111827; }
      .cancel:hover { background: #f3f4f6; }
      .exit { border: 0; background: #111827; color: #fff; }
      .exit:hover { opacity: 0.9; }
      .exit:disabled { opacity: 0.6; }
      @media (prefers-reduced-motion: reduce) { .dot .ping { animation: none; } .wrap { transition: none; } }
    `

    const host = document.createElement('div')
    host.id = 'ir-discount-preview'
    const shadow = host.attachShadow({ mode: 'open' })
    shadow.innerHTML = `
      <style>${STYLES}</style>
      <div class="wrap">
        <div class="chip">
          <button type="button" class="grip" aria-label="Move preview chip. Drag, or use the arrow keys.">
            <svg viewBox="0 0 10 16" fill="currentColor" aria-hidden="true"><circle cx="2.5" cy="3" r="1.5"/><circle cx="7.5" cy="3" r="1.5"/><circle cx="2.5" cy="8" r="1.5"/><circle cx="7.5" cy="8" r="1.5"/><circle cx="2.5" cy="13" r="1.5"/><circle cx="7.5" cy="13" r="1.5"/></svg>
          </button>
          <div class="label">
            <span class="dot"><span class="ping"></span><span></span></span>
            <span class="kicker">Preview</span>
            <span class="name"></span>
          </div>
          <button type="button" class="clear" aria-label="Clear discount rules preview" aria-haspopup="dialog" aria-expanded="false">
            <svg viewBox="0 0 30 30" fill="none" aria-hidden="true"><path d="M7.5 22.5L22.5 7.5M7.5 7.5L22.5 22.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
        </div>
        <div class="confirm" role="dialog" aria-label="Clear discount rules preview" hidden>
          <p class="question">Exit <b></b> preview?</p>
          <p class="note">The cart returns to the live discounts shoppers see.</p>
          <div class="actions">
            <button type="button" class="cancel">Cancel</button>
            <button type="button" class="exit">Clear preview</button>
          </div>
        </div>
      </div>
    `
    const $ = (selector) => shadow.querySelector(selector)
    const [wrap, chip, grip, clear, confirm, cancel, exit] = ['.wrap', '.chip', '.grip', '.clear', '.confirm', '.cancel', '.exit'].map($)
    $('.name').textContent = name
    $('.question b').textContent = name
    chip.title = `Previewing: ${name} (${id})`

    // Kept within the viewport, so a position saved on a wide screen still shows on a narrow one.
    const moveTo = (x, y) => {
      const { width, height } = chip.getBoundingClientRect()
      const left = Math.round(Math.min(Math.max(x, MARGIN), Math.max(MARGIN, window.innerWidth - width - MARGIN)))
      const top = Math.round(Math.min(Math.max(y, MARGIN), Math.max(MARGIN, window.innerHeight - height - MARGIN)))
      host.style.left = `${left}px`
      host.style.top = `${top}px`
      return { x: left, y: top }
    }
    const savePosition = (position) => storage.set(POSITION_KEY, JSON.stringify(position))
    const savedPosition = () => {
      try {
        const p = JSON.parse(storage.get(POSITION_KEY) ?? 'null')
        return Number.isFinite(p?.x) && Number.isFinite(p?.y) ? p : null
      } catch {
        return null
      }
    }
    let position = { x: MARGIN, y: MARGIN }

    grip.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return
      event.preventDefault()
      grip.setPointerCapture(event.pointerId)
      const offset = { x: event.clientX - position.x, y: event.clientY - position.y }
      wrap.classList.add('dragging')
      setOpen(false)
      const onMove = (e) => {
        position = moveTo(e.clientX - offset.x, e.clientY - offset.y)
      }
      const onUp = () => {
        wrap.classList.remove('dragging')
        grip.removeEventListener('pointermove', onMove)
        grip.removeEventListener('pointerup', onUp)
        grip.removeEventListener('pointercancel', onUp)
        savePosition(position)
      }
      grip.addEventListener('pointermove', onMove)
      grip.addEventListener('pointerup', onUp)
      grip.addEventListener('pointercancel', onUp)
    })
    const ARROWS = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }
    grip.addEventListener('keydown', (event) => {
      const step = ARROWS[event.key]
      if (!step) return
      event.preventDefault()
      const distance = event.shiftKey ? 50 : 10
      position = moveTo(position.x + step[0] * distance, position.y + step[1] * distance)
      savePosition(position)
    })
    window.addEventListener('resize', () => {
      position = moveTo(position.x, position.y)
    })

    const setOpen = (open) => {
      if (open) {
        // Open toward the larger side of the screen, so the popover stays visible wherever the chip sits.
        const rect = chip.getBoundingClientRect()
        confirm.classList.toggle('above', rect.top > window.innerHeight / 2)
        confirm.classList.toggle('right', rect.left + rect.width / 2 > window.innerWidth / 2)
      }
      confirm.hidden = !open
      clear.setAttribute('aria-expanded', String(open))
    }
    clear.addEventListener('click', () => setOpen(confirm.hidden))
    cancel.addEventListener('click', () => setOpen(false))
    document.addEventListener(
      'pointerdown',
      (event) => {
        if (!confirm.hidden && !event.composedPath().includes(host)) setOpen(false)
      },
      true,
    )
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape' || confirm.hidden) return
        // Capture, so Escape closes the popover before a drawer's own Escape handler sees it.
        event.stopPropagation()
        setOpen(false)
      },
      true,
    )

    exit.addEventListener('click', async () => {
      exit.disabled = cancel.disabled = true
      exit.textContent = 'Clearing…'
      try {
        // An attribute is only removed by writing it empty; leaving it out keeps it.
        const res = await fetch(`${root}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: { [PREVIEW_ATTRIBUTE]: '', [PREVIEW_NAME_ATTRIBUTE]: '' } }),
        })
        if (!res.ok) throw new Error(`cart/update.js responded ${res.status}`)
        storage.set(ACTIVE_KEY, null)
        // Reload so the cart and every price on the page come back from the live rules.
        window.location.reload()
      } catch (error) {
        console.error('[ir-discount-preview] Failed to clear the preview', error)
        exit.disabled = cancel.disabled = false
        exit.textContent = 'Clear preview'
      }
    })

    document.body.append(host)
    const start = savedPosition() ?? position
    position = moveTo(start.x, start.y)
    requestAnimationFrame(() => requestAnimationFrame(() => wrap.classList.add('visible')))
  }
})()
